import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import socket from '../utils/socket';
import { Spinner, EmptyState, Badge, statusBadgeMap, requestBadgeMap } from '../components/UI';
import { fmtDate, fmtDateTime, fmtPct } from '../utils/format';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';

const DOT = {
  login:  'var(--sms-dot-late)',
  logout: 'var(--sms-txt2)',
  create: 'var(--sms-dot-present)',
  update: 'var(--sms-acc)',
  delete: 'var(--sms-dot-absent)',
};

// Tiny inline trend line drawn behind a stat card's number. `series` is an
// array of { date, value }; renders nothing if fewer than 2 points (a flat
// line would be misleading, not informative).
function Sparkline({ series, color }) {
  if (!series || series.length < 2) return null;
  const w = 140, h = 34, pad = 3;
  const values = series.map(p => p.value);
  const min = Math.min(...values), max = Math.max(...values);
  const flat = max === min;
  // A flat series (e.g. 7 days of all-zero counts) mathematically plots at
  // the bottom edge, which reads as a decorative border rather than a
  // chart — center it in the strip instead so "no change" still looks like
  // a line, not a UI artifact stuck to the card's edge. (Dimming it with
  // opacity was tried here previously, but every --sms-stat-* token is only
  // pre-verified against WCAG 1.4.11's 3:1 non-text minimum at full opacity —
  // faded out, they fall to ~2:1 in both themes, so full opacity is kept.)
  const range = max - min || 1;
  const pts = series.map((p, i) => {
    const x = (i / (series.length - 1)) * w;
    const y = flat ? h / 2 : h - pad - ((p.value - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg className="sms-spark-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      {!flat && <path className="sms-spark-area" fill={color} d={areaPath} />}
      <path className="sms-spark-line" style={{ stroke: color }} d={linePath} />
      {!flat && <circle className="sms-spark-dot" style={{ fill: color }} cx={lx} cy={ly} r="2.5" />}
    </svg>
  );
}

function StatCard({ label, value, icon, color, sub, to, series }) {
  return (
    <Link to={to} className="sms-stat-card h-100 d-block" style={{
      textDecoration: 'none', cursor: 'pointer', position: 'relative', overflow: 'hidden',
      background: `linear-gradient(180deg, color-mix(in srgb, ${color} 7%, var(--sms-sur)), var(--sms-sur) 65%)`,
    }}>
      <div className="d-flex align-items-center justify-content-between mb-2" style={{ position: 'relative', zIndex: 1 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `color-mix(in srgb, ${color} 14%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`bi ${icon}`} style={{ color, fontSize: 16 }} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1, position: 'relative', zIndex: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 4, position: 'relative', zIndex: 1 }}>{sub}</div>}
      <Sparkline series={series} color={color} />
    </Link>
  );
}

// Tracks a CSS media query so components can pick real, differently-sized
// props (not just CSS-scaled pixels) for small screens — used to shrink the
// MUI donuts on mobile so their center-label text has proportionally more
// room, since scaling the rendered SVG via CSS alone doesn't change the
// coordinate space the label math runs in.
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

// Counts up from 0 to `target` on mount using an eased requestAnimationFrame
// loop — used by the live check-in number so it reads as "arriving" rather
// than a static figure. Skips straight to the final value when the user has
// asked their OS for reduced motion.
function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) { setValue(0); return; }
    if (prefersReducedMotion()) { setValue(target); return; }
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function fmtClockTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).slice(11, 16);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// Monotone cubic (Fritsch-Carlson) interpolation — same technique behind
// D3's curveMonotoneX. Draws a smooth curve through every point, but unlike
// a plain Catmull-Rom spline its tangents are clamped so the curve can never
// swing past the y-range of its two neighbouring points. Used for the
// attendance line: it needs to stay curved, but with 30 always-visible days
// (many carried-forward flat, with occasional sharp jumps) a Catmull-Rom
// spline was overshooting off the top of the chart.
function monotonePath(points) {
  const n = points.length;
  if (n < 2) return '';
  const xs = points.map(p => p[0]), ys = points.map(p => p[1]);

  const d = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1] - xs[i];
    d.push(dx === 0 ? 0 : (ys[i + 1] - ys[i]) / dx);
  }

  const m = new Array(n);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = (d[i - 1] === 0 || d[i] === 0 || (d[i - 1] < 0) !== (d[i] < 0)) ? 0 : (d[i - 1] + d[i]) / 2;
  }

  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / d[i], b = m[i + 1] / d[i];
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      m[i] = tau * a * d[i];
      m[i + 1] = tau * b * d[i];
    }
  }

  let path = `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1] - xs[i];
    const cp1x = xs[i] + dx / 3, cp1y = ys[i] + m[i] * dx / 3;
    const cp2x = xs[i + 1] - dx / 3, cp2y = ys[i + 1] - m[i + 1] * dx / 3;
    path += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${xs[i + 1].toFixed(1)},${ys[i + 1].toFixed(1)}`;
  }
  return path;
}

// Attendance % + new-enrolments trend, drawn as two animated lines that
// sweep in on load, with a pulsing "live" ring on the attendance line's
// latest point. Hovering (or touching, on mobile) tracks the nearest day
// and shows its exact values in a tooltip.
function TrendChart({ attendance, enrollments }) {
  const wrapRef = useRef(null);
  const attLineRef = useRef(null);
  const dotRef = useRef(null);
  const hoveringRef = useRef(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  hoveringRef.current = hoverIdx != null;

  // The "live" dot glides along the attendance line from left to right at a
  // steady *horizontal* pace — not along the curve's length. The line is spiky
  // (it shoots up and down between days), so travelling its length at a fixed
  // speed made the dot race and jerk. Here the x position eases in and out over
  // TRAVEL_MS, the y follows the line at that x, the dot fades in and out at the
  // ends, rests for REST_MS, then repeats. It hides while the chart is hovered
  // and is skipped entirely for reduced motion.
  useEffect(() => {
    const path = attLineRef.current;
    const dot = dotRef.current;
    if (!path || !dot || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const total = path.getTotalLength();
    const endX = path.getPointAtLength(total).x;
    // The path only ever moves rightwards, so x is monotonic in length and the
    // point at a given x can be found by bisection.
    const pointAtX = (px) => {
      let lo = 0, hi = total;
      for (let i = 0; i < 22; i++) {
        const mid = (lo + hi) / 2;
        if (path.getPointAtLength(mid).x < px) lo = mid; else hi = mid;
      }
      return path.getPointAtLength(hi);
    };
    const TRAVEL_MS = 9000, REST_MS = 1500, FADE = 0.07;
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2); // easeInOutQuad
    const startAt = performance.now() + 1800; // wait for the line's draw-in
    let raf;
    const tick = (now) => {
      const elapsed = now - startAt;
      if (elapsed < 0 || hoveringRef.current) {
        dot.style.opacity = 0;
      } else {
        const t = elapsed % (TRAVEL_MS + REST_MS);
        if (t > TRAVEL_MS) {
          dot.style.opacity = 0;
        } else {
          const p = t / TRAVEL_MS;
          const pt = pointAtX(ease(p) * endX);
          dot.setAttribute('cx', pt.x);
          dot.setAttribute('cy', pt.y);
          dot.style.opacity = Math.min(1, p / FADE, (1 - p) / FADE);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [attendance]);

  if (!attendance?.length) return <EmptyState icon="bi-graph-up" message="No trend data yet" />;

  // Chart area only — date labels render in their own HTML row below so
  // they never sit under the data lines (they used to overlap when the
  // attendance line ran flat near the bottom of the SVG).
  const w = 680, h = 170, pad = 10;
  const scaleY = (values) => {
    const min = Math.min(...values), max = Math.max(...values);
    const range = (max - min) || 1;
    return (v) => h - pad - ((v - min) / range) * (h - pad * 2);
  };
  // Covers the full 30-day window every day so the line never breaks — but a
  // day with no attendance session (no class held, has_data:false) plots as
  // 0, not the backend's carried-forward value, so it visually reads as "no
  // attendance" rather than falsely holding the last real day's percentage.
  const hasRealData = p => p.has_data !== false;
  const attendanceValues = attendance.map(p => hasRealData(p) ? p.value : 0);
  const yAttendance = scaleY(attendanceValues);
  const enrollSeries = enrollments?.length ? enrollments : attendance;
  const yEnroll = scaleY(enrollSeries.map(p => p.value));
  const n = attendance.length;
  const x = (i) => (i / (n - 1)) * w;

  const attendancePoints = attendanceValues.map((v, i) => [x(i), yAttendance(v)]);
  const attendancePath = monotonePath(attendancePoints);
  const areaPath = attendancePath
    ? `${attendancePath} L${attendancePoints[n - 1][0].toFixed(1)},${h} L${attendancePoints[0][0].toFixed(1)},${h} Z`
    : '';
  const enrollPath = enrollments?.length
    ? monotonePath(enrollments.map((p, i) => [x(i), yEnroll(p.value)]))
    : null;

  const lastX = attendancePoints[n - 1][0];
  const lastY = attendancePoints[n - 1][1];
  const firstDate = fmtDate(attendance[0].date);
  const midDate   = fmtDate(attendance[Math.floor(n / 2)].date);

  const updateHover = (clientX) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setHoverIdx(Math.round(frac * (n - 1)));
  };

  const hover = hoverIdx != null ? {
    idx: hoverIdx,
    date: fmtDate(attendance[hoverIdx].date),
    attendanceVal: attendance[hoverIdx].value,
    hasAttendance: hasRealData(attendance[hoverIdx]),
    enrollVal: enrollSeries[hoverIdx]?.value,
  } : null;

  // Tooltip flips to the left of the cursor past the chart's midpoint so it
  // never runs off the right edge of the card.
  const hoverPctX = hover ? (hover.idx / (n - 1)) * 100 : 0;
  const flipLeft = hoverPctX > 55;

  return (
    <>
      <div
        ref={wrapRef}
        style={{ position: 'relative' }}
        onMouseMove={(e) => updateHover(e.clientX)}
        onMouseLeave={() => setHoverIdx(null)}
        onTouchMove={(e) => e.touches[0] && updateHover(e.touches[0].clientX)}
        onTouchEnd={() => setHoverIdx(null)}
      >
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }} aria-hidden="true">
          <line className="sms-trend-grid" x1="0" y1={pad} x2={w} y2={pad} stroke="var(--sms-bdr)" strokeWidth="1" style={{ animationDelay: '0s' }} />
          <line className="sms-trend-grid" x1="0" y1={h * 0.375} x2={w} y2={h * 0.375} stroke="var(--sms-bdr)" strokeWidth="1" style={{ animationDelay: '.08s' }} />
          <line className="sms-trend-grid" x1="0" y1={h * 0.625} x2={w} y2={h * 0.625} stroke="var(--sms-bdr)" strokeWidth="1" style={{ animationDelay: '.16s' }} />
          <line className="sms-trend-grid" x1="0" y1={h - pad} x2={w} y2={h - pad} stroke="var(--sms-bdr)" strokeWidth="1" style={{ animationDelay: '.24s' }} />

          {areaPath && <path className="sms-trend-area" fill="var(--sms-acc)" d={areaPath} />}
          {attendancePath && <path ref={attLineRef} className="sms-trend-line" style={{ stroke: 'var(--sms-acc)' }} d={attendancePath} />}
          {enrollPath && <path className="sms-trend-line sms-trend-line--secondary" style={{ stroke: 'var(--sms-stat-amber)' }} d={enrollPath} />}

          {attendancePath && (
            <circle ref={dotRef} className="sms-trend-travel" r="4" fill="var(--sms-acc)" cx="0" cy="0" />
          )}

          {lastX != null && (
            <>
              <circle className="sms-trend-endpoint-ring" style={{ stroke: 'var(--sms-acc)' }} cx={lastX} cy={lastY} r="5" />
              <circle fill="var(--sms-acc)" cx={lastX} cy={lastY} r="4" />
            </>
          )}

          {hover && (
            <>
              <line x1={x(hover.idx)} y1={pad} x2={x(hover.idx)} y2={h - pad} stroke="var(--sms-txt2)" strokeWidth="1" strokeDasharray="3 3" />
              {hover.hasAttendance && (
                <circle cx={x(hover.idx)} cy={yAttendance(hover.attendanceVal)} r="4.5" fill="var(--sms-sur)" stroke="var(--sms-acc)" strokeWidth="2" />
              )}
              {hover.enrollVal != null && (
                <circle cx={x(hover.idx)} cy={yEnroll(hover.enrollVal)} r="4.5" fill="var(--sms-sur)" stroke="var(--sms-stat-amber)" strokeWidth="2" />
              )}
            </>
          )}
        </svg>

        {hover && (
          <div
            style={{
              position: 'absolute', top: 6, pointerEvents: 'none',
              left: flipLeft ? 'auto' : `${hoverPctX}%`,
              right: flipLeft ? `${100 - hoverPctX}%` : 'auto',
              transform: flipLeft ? 'translateX(10px)' : 'translateX(-10px)',
              background: 'var(--sms-sur)', border: '1px solid var(--sms-bdr)', borderRadius: 8,
              padding: '8px 10px', boxShadow: 'var(--sms-shadow)', minWidth: 130, zIndex: 2,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt)', marginBottom: 4 }}>{hover.date}</div>
            <div className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sms-acc)', display: 'inline-block' }} />
              {hover.hasAttendance
                ? <>Attendance: <strong style={{ color: 'var(--sms-txt)', fontWeight: 600 }}>{fmtPct(hover.attendanceVal)}</strong></>
                : <>No session that day</>}
            </div>
            {hover.enrollVal != null && (
              <div className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--sms-stat-amber)', display: 'inline-block' }} />
                Enrolments: <strong style={{ color: 'var(--sms-txt)', fontWeight: 600 }}>{hover.enrollVal}</strong>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="d-flex justify-content-between" style={{ marginTop: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{firstDate}</span>
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{midDate}</span>
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)', fontWeight: 600 }}>Today</span>
      </div>
    </>
  );
}

function LiveCheckinCard({ data }) {
  const total   = data?.total_active || 0;
  const checked = useCountUp(data?.checked_in || 0);
  const pct     = total > 0 ? Math.round(((data?.checked_in || 0) / total) * 100) : 0;
  const recent  = data?.recent || [];
  const [tickIdx, setTickIdx] = useState(0);

  useEffect(() => {
    if (recent.length < 2 || prefersReducedMotion()) return;
    const id = setInterval(() => setTickIdx(i => (i + 1) % recent.length), 2800);
    return () => clearInterval(id);
  }, [recent.length]);

  const current = recent[tickIdx];

  return (
    <div className="sms-card h-100 sms-widget-fade">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Checked in today</h6>
        <span className="badge rounded-pill badge-present d-inline-flex align-items-center gap-1" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px' }}>
          <span className="sms-live-pulse" />Live
        </span>
      </div>

      <div className="d-flex align-items-end gap-2">
        <span style={{ fontSize: 40, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{checked}</span>
        <span style={{ fontSize: 13, color: 'var(--sms-txt2)', marginBottom: 6 }}>of {total} enrolled</span>
      </div>

      <div className="sms-progress" style={{ marginTop: 14 }}>
        <div className="sms-count-fill" style={{ '--sms-count-pct': `${pct}%` }} />
      </div>
      <div className="d-flex justify-content-between mt-1">
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{fmtPct(pct)} arrived</span>
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
          {data?.last_checkin ? `Last: ${fmtClockTime(data.last_checkin)}` : 'No check-ins yet'}
        </span>
      </div>

      {current && (
        <div className="d-flex align-items-center gap-2 mt-3 pt-2" style={{ borderTop: '1px solid var(--sms-bdr)' }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--sms-acc-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="bi bi-check-lg" style={{ color: 'var(--sms-acc)', fontSize: 12 }} />
          </div>
          <span key={tickIdx} className="sms-ticker-row" style={{ fontSize: 12, color: 'var(--sms-txt2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fmtClockTime(current.clock_in_time)} · <strong style={{ color: 'var(--sms-txt)', fontWeight: 600 }}>{current.name}</strong> — {current.intake || '—'}
          </span>
        </div>
      )}
    </div>
  );
}

function getPctColor(pct) {
  if (pct >= 85) return 'var(--sms-dot-present)';
  if (pct >= 70) return 'var(--sms-dot-late)';
  return 'var(--sms-dot-absent)';
}

function getStatusLabel(pct) {
  if (pct >= 85) return { label: 'On Track', cls: 'badge-present' };
  if (pct >= 70) return { label: 'Monitor',  cls: 'badge-late' };
  return                 { label: 'At Risk',  cls: 'badge-absent' };
}

// Horizontal pass-rate bar per subject, with the average score plotted as a
// small marker on top of the bar — lets a reader compare subjects at a
// glance without reading the detail table row by row.
function AcademicBarChart({ rows }) {
  if (!rows.length) return null;
  const maxScore = Math.max(100, ...rows.map(r => parseFloat(r.avg_score) || 0));
  return (
    <div className="d-flex flex-column gap-3 mb-4">
      {rows.map((row, i) => {
        const pct = parseFloat(row.pass_rate) || 0;
        const avg = parseFloat(row.avg_score) || 0;
        const color = getPctColor(pct);
        return (
          <div key={i} className="d-flex align-items-center gap-3">
            <div style={{ width: 128, flexShrink: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--sms-txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.subject}>
              {row.subject}
            </div>
            <div style={{ flex: 1, position: 'relative', height: 18 }}>
              <div style={{ position: 'absolute', inset: 0, background: 'var(--sms-sur2)', borderRadius: 5 }} />
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: color, borderRadius: 5, transition: 'width .5s ease' }} />
              <div title={`Avg score ${avg}`} style={{
                position: 'absolute', top: -3, left: `calc(${Math.min(100, avg / maxScore * 100)}% - 1px)`,
                width: 2, height: 24, background: 'var(--sms-txt)', opacity: .55, borderRadius: 1,
              }} />
            </div>
            <div style={{ width: 44, flexShrink: 0, textAlign: 'right', fontSize: 12.5, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(pct)}</div>
          </div>
        );
      })}
      <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
        <span style={{ width: 2, height: 10, background: 'var(--sms-txt)', opacity: .55, display: 'inline-block' }} />
        vertical mark = average score
      </div>
    </div>
  );
}

// Center label overlay for a Recharts-style donut — MUI's PieChart renders
// children inside its own <svg>, so this reads the chart's actual drawing
// area rather than guessing coordinates. Long labels (e.g. "withdrawn/deferred")
// used to overflow past the donut's own viewBox at a fixed font-size — split
// them onto two lines at the "/" and shrink the font so they always fit
// inside the inner radius, regardless of how small the chart is rendered.
function PieCenterLabel({ label, value }) {
  const { width, height, left, top } = useDrawingArea();
  const cx = left + width / 2, cy = top + height / 2;
  const parts = label.includes('/') ? label.split('/') : [label];
  const labelFontSize = parts.length > 1 || label.length > 12 ? 9.5 : 11;
  const valueFontSize = width < 120 ? 18 : 22;
  const labelLineHeight = labelFontSize + 2;
  const labelStartY = cy - 6 - ((parts.length - 1) * labelLineHeight) / 2;
  return (
    <>
      {parts.map((part, i) => (
        <text key={i} x={cx} y={labelStartY + i * labelLineHeight} textAnchor="middle" dominantBaseline="middle"
          style={{ fontSize: labelFontSize, fill: 'var(--sms-txt2)' }}>{part}</text>
      ))}
      <text x={cx} y={cy + 14} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: valueFontSize, fontWeight: 700, fill: 'var(--sms-txt)' }}>{value}</text>
    </>
  );
}

// Donut chart built on @mui/x-charts (installed specifically for this) —
// hand-rolled SVG arcs kept not landing visually, so this uses a real
// charting library's Pie/donut implementation instead.
//
// Two things MUI doesn't do for you here, both of which showed up as the
// center label (and the ring itself) drifting/clipping on the smaller mobile
// `size`: (1) PieChart reserves margin for its legend by default even when
// `slotProps.legend.hidden` is set, which off-centers the drawing area that
// both the ring and <PieCenterLabel> are positioned within — an explicit
// zero margin removes that reserved space; (2) innerRadius/outerRadius are
// absolute px, so they must scale with `size` or a fixed radius overflows a
// smaller canvas.
function MuiDonut({ segments, centerLabel, centerValue, size = 160 }) {
  const data = segments.map(s => ({ id: s.key, value: s.value, label: s.label, color: s.color }));
  const outerRadius = size * 0.45, innerRadius = size * 0.3;
  return (
    <PieChart
      series={[{ data, innerRadius, outerRadius, paddingAngle: 3, cornerRadius: 6, highlightScope: { fade: 'global', highlight: 'item' } }]}
      width={size} height={size}
      margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
      slotProps={{ legend: { hidden: true } }}
    >
      <PieCenterLabel label={centerLabel} value={centerValue} />
    </PieChart>
  );
}

// Plain dot legend to sit beside the donut — count + share per segment,
// without duplicating the ring itself.
function DonutLegend({ segments, total }) {
  return (
    <div className="d-flex flex-column gap-2 sms-donut-legend">
      {segments.map(s => (
        <div key={s.key} className="d-flex align-items-center gap-2" style={{ fontSize: 12.5 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
          <span style={{ color: 'var(--sms-txt2)', minWidth: 68 }}>{s.label}</span>
          <span style={{ fontWeight: 700, color: 'var(--sms-txt)', fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
          <span style={{ color: 'var(--sms-txt2)', fontSize: 12 }}>({Math.round(s.value / total * 100)}%)</span>
        </div>
      ))}
    </div>
  );
}

// Present/Late/Absent/MC totals across every intake's last-30-day attendance
// — the per-intake bars above break this down by cohort, this rolls it up
// into one "how are we doing overall" breakdown.
function AttendanceDonut({ rows }) {
  const totals = rows.reduce((acc, r) => ({
    present: acc.present + (parseInt(r.present) || 0),
    late:    acc.late    + (parseInt(r.late) || 0),
    absent:  acc.absent  + (parseInt(r.absent) || 0),
    mc:      acc.mc      + (parseInt(r.mc) || 0),
  }), { present: 0, late: 0, absent: 0, mc: 0 });
  const total = totals.present + totals.late + totals.absent + totals.mc;
  if (!total) return <EmptyState icon="bi-calendar" message="No attendance data in the last 30 days" />;

  const segments = [
    { key: 'present', label: 'Present', value: totals.present, color: 'var(--sms-dot-present)' },
    { key: 'late',    label: 'Late',    value: totals.late,    color: 'var(--sms-dot-late)' },
    { key: 'absent',  label: 'Absent',  value: totals.absent,  color: 'var(--sms-dot-absent)' },
    { key: 'mc',      label: 'MC',      value: totals.mc,      color: 'var(--sms-dot-mc)' },
  ];
  const chartSegments = segments.filter(s => s.value > 0);

  const attendedPct = total > 0
    ? Math.round(((totals.present + totals.late + totals.mc) - totals.absent) / total * 100)
    : 0;
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <div className="d-flex align-items-center gap-4 flex-wrap sms-donut-row">
      <div className="sms-donut-chart"><MuiDonut segments={chartSegments} centerLabel="Attended" centerValue={`${fmtPct(attendedPct)}`} size={isMobile ? 128 : 160} /></div>
      <DonutLegend segments={segments} total={total} />
    </div>
  );
}

const STATUS_META = {
  registered: { label: 'Registered', color: 'var(--sms-stat-cyan)' },
  active:     { label: 'Active',     color: 'var(--sms-dot-present)' },
  inactive:   { label: 'Inactive',   color: 'var(--sms-divider)' },
  deferred:   { label: 'Deferred',   color: 'var(--sms-dot-late)' },
  withdrawn:  { label: 'Withdrawn',  color: 'var(--sms-dot-absent)' },
  graduated:  { label: 'Graduated',  color: 'var(--sms-stat-violet)' },
};
const STATUS_ORDER = ['registered', 'active', 'inactive', 'withdrawn', 'deferred', 'graduated'];

// Whole-cohort status split. Keep zero-count configured statuses in the legend
// so the dashboard shows the complete set of statuses used by the system.
function StudentStatusDonut({ rows }) {
  const total = rows.reduce((s, r) => s + (parseInt(r.total) || 0), 0);
  if (!total) return <EmptyState icon="bi-people" message="No students yet" />;

  const counts = Object.fromEntries(rows.map(r => [r.status, parseInt(r.total) || 0]));
  const segments = [
    ...STATUS_ORDER.map(key => ({ key, value: counts[key] || 0, ...STATUS_META[key] })),
    ...rows
      .filter(r => !STATUS_META[r.status])
      .map(r => ({ key: r.status, value: parseInt(r.total) || 0, label: r.status, color: 'var(--sms-txt2)' })),
  ];
  const chartSegments = segments.filter(s => s.value > 0);

  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <div className="d-flex align-items-center gap-4 flex-wrap sms-donut-row">
      <div className="sms-donut-chart"><MuiDonut segments={chartSegments} centerLabel="Total students" centerValue={total} size={isMobile ? 128 : 160} /></div>
      <DonutLegend segments={segments} total={total} />
    </div>
  );
}

// Grouped weekday bars for the two counts that otherwise only live as tiny
// stat-card sparklines — seeing disciplinary cases against open requests
// side by side surfaces days that were unusually rough at a glance.
function WeekGlanceChart({ disciplinary, requests }) {
  if (!disciplinary?.length) return <EmptyState icon="bi-bar-chart" message="No data yet" />;
  const days = disciplinary.map((p, i) => ({
    label: new Date(p.date + 'T00:00:00').toLocaleDateString('en-SG', { weekday: 'short' }),
    disciplinary: p.value,
    requests: requests?.[i]?.value ?? 0,
  }));
  const max = Math.max(1, ...days.flatMap(d => [d.disciplinary, d.requests]));
  const h = 110;
  return (
    <div className="sms-week-glance">
      <div className="d-flex align-items-end gap-2 sms-week-days" style={{ height: h + 10, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: `0 0 20px 0`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
          <div style={{ borderTop: '1px dashed var(--sms-bdr)' }} />
          <div style={{ borderTop: '1px dashed var(--sms-bdr)' }} />
          <div style={{ borderTop: '1px solid var(--sms-bdr)' }} />
        </div>
        {days.map((d, i) => (
          <div key={i} className="d-flex flex-column align-items-center" style={{ flex: 1, position: 'relative', minWidth: 0 }}>
            <div className="d-flex align-items-end justify-content-center gap-1" style={{
              height: h, width: '100%', background: 'var(--sms-sur2)', borderRadius: 8, padding: '0 clamp(2px, 1.5vw, 6px)',
            }}>
              <div title={`${d.disciplinary} disciplinary`} style={{
                width: 'clamp(9px, 3vw, 18px)', height: `${Math.max((d.disciplinary / max) * (h - 12), d.disciplinary ? 4 : 2)}px`,
                background: 'var(--sms-stat-red)', opacity: d.disciplinary ? 1 : .35, borderRadius: '5px 5px 0 0', transition: 'height .4s ease',
              }} />
              <div title={`${d.requests} requests`} style={{
                width: 'clamp(9px, 3vw, 18px)', height: `${Math.max((d.requests / max) * (h - 12), d.requests ? 4 : 2)}px`,
                background: 'var(--sms-stat-amber)', opacity: d.requests ? 1 : .35, borderRadius: '5px 5px 0 0', transition: 'height .4s ease',
              }} />
            </div>
            <span style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 6, fontWeight: 500 }}>{d.label}</span>
          </div>
        ))}
      </div>
      <div className="d-flex gap-3 mt-3 flex-wrap">
        <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--sms-stat-red)', display: 'inline-block' }} />Disciplinary cases
        </span>
        <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--sms-stat-amber)', display: 'inline-block' }} />Open requests
        </span>
      </div>
    </div>
  );
}

// Month-by-month enrolments, this year vs last year — hand-rolled, same
// dual-bar layout as WeekGlanceChart above (traded away from MUI's BarChart:
// its default legend ran oversized and its hover tooltip portals outside
// React/CSS reach, both fights not worth having twice). Hover here is a pure
// CSS tooltip (.sms-chart-tooltip, via the column's data-tooltip + ::after)
// instead of the browser's native `title`, which has a slow, un-stylable
// ~600ms-1s OS hover delay — this one is instant and animates in.
// Series colors var(--sms-stat-blue)/var(--sms-stat-amber) are ≥4.5:1
// against --sms-sur in both light and dark mode (see index.css header),
// same tokens the stat cards already use.
function EnrollmentYoYChart({ monthly, currentYear, previousYear }) {
  if (!monthly?.length) return <EmptyState icon="bi-bar-chart" message="No data yet" />;
  const max = Math.max(1, ...monthly.flatMap(m => [m.current, m.previous]));
  const h = 130;
  return (
    <div className="sms-week-glance">
      <div className="d-flex align-items-end gap-2 sms-week-days sms-enrollment-days" style={{ height: h + 10, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: `0 0 20px 0`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
          <div style={{ borderTop: '1px dashed var(--sms-bdr)' }} />
          <div style={{ borderTop: '1px dashed var(--sms-bdr)' }} />
          <div style={{ borderTop: '1px solid var(--sms-bdr)' }} />
        </div>
        {monthly.map((m, i) => (
          <div key={i} className="d-flex flex-column align-items-center" style={{ flex: 1, position: 'relative', minWidth: 0 }}>
            <div
              className="sms-chart-col"
              style={{ height: h, width: '100%', background: 'var(--sms-sur2)', borderRadius: 8, padding: '0 clamp(1px, 1vw, 6px)' }}
            >
              <div className="sms-chart-tooltip">
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{m.label}</div>
                <div className="d-flex align-items-center gap-1">
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: 'var(--sms-stat-blue)', display: 'inline-block' }} />
                  {currentYear}: <b>{m.current}</b>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: 'var(--sms-stat-amber)', display: 'inline-block' }} />
                  {previousYear}: <b>{m.previous}</b>
                </div>
              </div>
              <div className="d-flex align-items-end justify-content-center gap-1 h-100">
                <div className="sms-chart-bar" style={{
                  width: 'clamp(6px, 2vw, 14px)', '--bar-h': `${Math.max((m.previous / max) * (h - 12), m.previous ? 4 : 2)}px`,
                  background: 'var(--sms-stat-amber)', opacity: m.previous ? 1 : .3, animationDelay: `${i * 0.03}s`,
                }} />
                <div className="sms-chart-bar" style={{
                  width: 'clamp(6px, 2vw, 14px)', '--bar-h': `${Math.max((m.current / max) * (h - 12), m.current ? 4 : 2)}px`,
                  background: 'var(--sms-stat-blue)', opacity: m.current ? 1 : .3, animationDelay: `${i * 0.03 + 0.05}s`,
                }} />
              </div>
            </div>
            <span style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 6, fontWeight: 500 }}>{m.label}</span>
          </div>
        ))}
      </div>
      <div className="d-flex gap-3 mt-2 flex-wrap">
        <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--sms-stat-amber)', display: 'inline-block' }} />{previousYear}
        </span>
        <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--sms-stat-blue)', display: 'inline-block' }} />{currentYear}
        </span>
      </div>
    </div>
  );
}

// Today formatted
const TODAY = new Date().toLocaleDateString('en-SG', { weekday:'long', day:'2-digit', month:'long', year:'numeric' });
const SEM   = (() => {
  const m = new Date().getMonth() + 1;
  const y = new Date().getFullYear();
  return m <= 6 ? `Semester 1, AY${y}/${y+1}` : `Semester 2, AY${y}/${y+1}`;
})();

export default function DashboardPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard')
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  // Joins the 'dashboard' socket room so every widget on this page (stats,
  // sparklines, trend chart, check-ins, recent lists) updates live instead of
  // only reflecting whatever was true when the page first loaded.
  useEffect(() => {
    if (!socket.connected) socket.connect();
    const onConnect = () => socket.emit('join_dashboard', null, (res) => {
      if (res?.success) setData(res.data);
    });
    const onDashboardUpdate = (payload) => setData(payload);

    socket.on('connect', onConnect);
    socket.on('dashboard_update', onDashboardUpdate);
    if (socket.connected) onConnect();

    return () => {
      socket.emit('leave_dashboard');
      socket.off('connect', onConnect);
      socket.off('dashboard_update', onDashboardUpdate);
    };
  }, []);

  if (loading) return <Spinner />;
  if (!data)   return <EmptyState message="Could not load dashboard" />;

  const {
    stats, recent_students, intake_capacity,
    recent_audit, pending_requests,
    attendance_by_intake, academic_by_subject,
    sparklines, trend, checkin_today,
    student_status_breakdown, complaints_feedback,
  } = data;

  return (
    <>
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1 fw-bold" style={{ color: 'var(--sms-txt)', fontSize: 20 }}>
          Administrator Dashboard
        </h5>
        <p style={{ fontSize: 13, color: 'var(--sms-txt2)', marginBottom: 0 }}>
          Tourism Management Institute of Singapore — {SEM}
          <span style={{ margin: '0 8px', color: 'var(--sms-txt2)' }}>|</span>
          As at {TODAY}
        </p>
      </div>

      {/* Stat cards — 4 across, ordered most → least important to check */}
      <div className="row g-3 mb-4 sms-stat-anim-row">
        <div className="col-6 col-md-3">
          <StatCard label="Disciplinary" value={stats.disciplinary} icon="bi-exclamation-triangle" color="var(--sms-stat-red)"
            sub="Active this month" to="/records/disciplinary" series={sparklines?.disciplinary} />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Open Requests" value={stats.open_requests} icon="bi-envelope-open" color="var(--sms-stat-amber)"
            sub="Pending review" to="/records/requests" series={sparklines?.open_requests} />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Avg Attendance" value={stats.avg_attendance != null ? `${fmtPct(stats.avg_attendance)}` : '—'}
            icon="bi-calendar-check" color="var(--sms-stat-green)" sub="Last 30 days" to="/students/attendance" series={sparklines?.avg_attendance} />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Class Passing Rate" value={stats.overall_pass_rate != null ? `${fmtPct(stats.overall_pass_rate)}` : '—'}
            icon="bi-graph-up" color="var(--sms-stat-violet)"
            sub={stats.top_passing_module
              ? `Best: ${stats.top_passing_module.module_name} (${stats.top_passing_module.rate}%) — ${stats.top_passing_module.course_name || stats.top_passing_module.course_code} / ${stats.top_passing_module.intake_code}`
              : 'Fully-marked students, all intakes'}
            to={stats.top_passing_module
              ? `/reports/passing-rate?course=${encodeURIComponent(stats.top_passing_module.course_code)}&intake=${encodeURIComponent(stats.top_passing_module.intake_code)}`
              : '/reports/passing-rate'} />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Total Students" value={stats.total_students} icon="bi-people-fill" color="var(--sms-stat-blue)"
            sub={`${stats.active_intakes} active intakes`} to="/students/profile" />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Alumni Tracked" value={stats.alumni_count} icon="bi-award" color="var(--sms-stat-cyan)"
            sub="All cohorts" to="/records/alumni" />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Trainer Claims" value={stats.pending_trainer_claims} icon="bi-cash-coin" color="var(--sms-stat-amber)"
            sub="Awaiting review" to="/trainers/claims" />
        </div>
        <div className="col-6 col-md-3">
          <StatCard label="Assessment Reviews" value={stats.pending_assessment_reviews} icon="bi-clipboard-check" color="var(--sms-stat-violet)"
            sub="Submitted by trainers" to="/trainers/assessments" />
        </div>
      </div>

      {/* Row 1a: Enrolments this year vs last year, month by month — ranked
          right after the top trend chart since it's the headline growth
          metric, ahead of the more operational/day-to-day widgets below. */}
      <div className="row g-3 mb-3">
        <div className="col-12">
          <div className="sms-card sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>
                Enrolments — {stats.enrollment_yoy?.current_year} vs {stats.enrollment_yoy?.previous_year}
              </h6>
              {stats.enrollment_yoy?.change_pct != null && (
                <span style={{ fontSize: 12, fontWeight: 600, color: stats.enrollment_yoy.change_pct >= 0 ? 'var(--sms-stat-green)' : 'var(--sms-stat-red)' }}>
                  <i className={`bi ${stats.enrollment_yoy.change_pct >= 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'}`} />
                  {' '}{stats.enrollment_yoy.change_pct >= 0 ? '+' : ''}{fmtPct(stats.enrollment_yoy.change_pct)} YoY
                </span>
              )}
            </div>
            <EnrollmentYoYChart monthly={stats.enrollment_yoy?.monthly} currentYear={stats.enrollment_yoy?.current_year} previousYear={stats.enrollment_yoy?.previous_year} />
          </div>
        </div>
      </div>

      {/* Row 1b: Trend chart + live check-in counter */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-8">
          <div className="sms-card sms-widget-fade">
            <div className="d-flex align-items-start justify-content-between mb-1">
              <div>
                <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Attendance &amp; Enrolment Trend</h6>
                <p style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2, marginBottom: 0 }}>Last 30 days · updates daily</p>
              </div>
              <div className="d-flex gap-3">
                <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                  <span className="sms-legend-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sms-acc)', display: 'inline-block' }} />Attendance %
                </span>
                <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                  <span className="sms-legend-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sms-stat-amber)', display: 'inline-block', animationDelay: '.3s' }} />New enrolments
                </span>
              </div>
            </div>
            <TrendChart attendance={trend?.attendance} enrollments={trend?.enrollments} />
          </div>
        </div>
        <div className="col-12 col-xl-4">
          <LiveCheckinCard data={checkin_today} />
        </div>
      </div>

      {/* Row 2: Attendance by intake + overall attendance breakdown */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-8">
          <div className="sms-card h-100 sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold d-flex align-items-center gap-1" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>
                Attendance by intake
                <i className="bi bi-info-circle" style={{ fontSize: 12, color: 'var(--sms-txt2)', cursor: 'help' }}
                  title="Rate = (Present + Late + MC − Absent) ÷ Total Sessions, over the last 30 days." />
              </h6>
              <Link to="/students/attendance" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            {!attendance_by_intake.length ? (
              <EmptyState icon="bi-calendar" message="No attendance data in the last 30 days" />
            ) : (
              <div className="d-flex flex-column gap-3">
                {attendance_by_intake.map(a => {
                  const pct = parseFloat(a.attendance_pct) || 0;
                  const color = getPctColor(pct);
                  return (
                    <div key={a.intake_code}>
                      <div className="d-flex justify-content-between align-items-center mb-1" style={{ flexWrap: 'wrap', rowGap: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--sms-txt)' }}>{a.intake_code}</span>
                        <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                            P:{a.present} L:{a.late} A:{a.absent} MC:{a.mc || 0}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color, minWidth: 38, textAlign: 'right' }}>
                            {fmtPct(pct)}
                          </span>
                        </div>
                      </div>
                      <div className="sms-progress">
                        <div className="sms-progress-fill" style={{ width: `${pct}%`, background: color, transition: 'width .4s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="col-12 col-xl-4">
          <div className="sms-card h-100 sms-widget-fade">
            <div className="mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Attendance breakdown</h6>
              <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2 }}>Last 30 days up to today</div>
            </div>
            <AttendanceDonut rows={attendance_by_intake} />
          </div>
        </div>
      </div>

      {/* Row 2b: This week at a glance + student status composition */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-6">
          <div className="sms-card h-100 sms-widget-fade">
            <h6 className="mb-3 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>This week at a glance</h6>
            <WeekGlanceChart disciplinary={sparklines?.disciplinary} requests={sparklines?.open_requests} />
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="sms-card h-100 sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Student status</h6>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2 }}>All students · current status</div>
              </div>
              <Link to="/students/profile" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            <StudentStatusDonut rows={student_status_breakdown || []} />
          </div>
        </div>
      </div>

      {/* Row 3: Academic performance by subject + pending requests */}
      <div className="row g-3 mb-3">
        {/* Academic performance */}
        <div className="col-12 col-xl-8">
          <div className="sms-card sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>
                Academic performance by subject
              </h6>
              <Link to="/students/academic" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>{SEM}</Link>
            </div>
            {!academic_by_subject.length ? (
              <EmptyState icon="bi-graph-up" message="No academic records yet" />
            ) : (
              <>
              <AcademicBarChart rows={academic_by_subject} />
              <div style={{ overflowX: 'auto' }}>
                <table className="sms-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Intake</th>
                      <th style={{ textAlign: 'center' }}>Enroled</th>
                      <th style={{ textAlign: 'center' }}>Avg Score</th>
                      <th style={{ textAlign: 'center' }}>Pass Rate</th>
                      <th style={{ textAlign: 'center' }}>At Risk</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {academic_by_subject.map((row, i) => {
                      const pct   = parseFloat(row.pass_rate) || 0;
                      const avg   = parseFloat(row.avg_score) || 0;
                      const { label, cls } = getStatusLabel(pct);
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>{row.subject}</td>
                          <td>{row.intake_code || '—'}</td>
                          <td style={{ textAlign: 'center', fontSize: 13 }}>{row.enrolled}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--sms-acc)', fontSize: 13 }}>{avg}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge rounded-pill ${cls}`} style={{ fontSize: 12 }}>{fmtPct(pct)}</span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: row.at_risk > 0 ? 'var(--sms-dot-absent)' : 'var(--sms-txt2)', fontSize: 13 }}>
                            {row.at_risk}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`badge rounded-pill ${cls}`} style={{ fontSize: 12 }}>{label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>

        {/* Pending requests */}
        <div className="col-12 col-xl-4">
          <div className="sms-card h-100 sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Pending requests</h6>
              <Link to="/records/requests" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            {!pending_requests.length ? <EmptyState message="No pending requests" icon="bi-envelope-open" /> : (
              <div className="d-flex flex-column">
                {pending_requests.map((r, i) => (
                  <div key={r.id} className="d-flex align-items-center gap-2 py-2"
                    style={{ borderBottom: i < pending_requests.length - 1 ? '1px solid var(--sms-bdr)' : 'none' }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                      background: r.priority === 'urgent' ? 'var(--sms-danger-bg-soft)' : 'var(--sms-sur2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="bi bi-envelope" style={{ fontSize: 14, color: r.priority === 'urgent' ? 'var(--sms-dot-absent)' : 'var(--sms-txt2)' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.request_type}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{r.student_id} · {r.student_name} · {r.intake_code || '—'}</div>
                    </div>
                    <Badge value={r.status} map={requestBadgeMap} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 3b: Complaints & Feedback awaiting action */}
      <div className="row g-3 mb-3">
        <div className="col-12">
          <div className="sms-card sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Complaints &amp; Feedback</h6>
              <Link to="/records/requests" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            {!complaints_feedback?.length ? (
              <EmptyState message="No complaints or feedback awaiting action" icon="bi-chat-square-text" />
            ) : (
              <div className="row g-2">
                {complaints_feedback.map(c => (
                  <div key={c.id} className="col-12 col-md-6 col-xl-4">
                    <div className="d-flex align-items-center gap-2 p-2" style={{ border: '1px solid var(--sms-bdr)', borderRadius: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                        background: c.priority === 'urgent' ? 'var(--sms-danger-bg-soft)' : 'var(--sms-sur2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={`bi ${c.request_type === 'Complaint' ? 'bi-exclamation-circle' : 'bi-chat-left-text'}`}
                          style={{ fontSize: 14, color: c.priority === 'urgent' ? 'var(--sms-dot-absent)' : 'var(--sms-txt2)' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt)' }}>{c.request_type}</span>
                          <Badge value={c.status} map={requestBadgeMap} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--sms-txt2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.student_name || c.student_id} · {c.intake_code || '—'}
                        </div>
                      </div>
                      <Link to="/records/requests" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none', flexShrink: 0 }}>Resolve →</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 4: Recent students + Intake capacity */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-7">
          <div className="sms-card sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Recent students</h6>
              <Link to="/students" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            {!recent_students.length ? <EmptyState /> : (
              <div style={{ overflowX: 'auto' }}>
                <table className="sms-table">
                  <thead><tr><th>Name</th><th>Intake</th><th>Status</th></tr></thead>
                  <tbody>
                    {recent_students.map(s => (
                      <tr key={s.student_no}>
                        <td style={{ fontWeight: 500 }}>{s.name}</td>
                        <td>{s.intake || '—'}</td>
                        <td><Badge value={s.status} map={statusBadgeMap} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="sms-card h-100 sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Intake capacity</h6>
              <Link to="/records/intakes" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            {!intake_capacity.length ? <EmptyState /> : (
              <div className="d-flex flex-column gap-3">
                {intake_capacity.map(i => {
                  const pct = Math.round((i.enrolled / i.capacity) * 100);
                  return (
                    <div key={i.intake_code}>
                      <div className="d-flex justify-content-between mb-1">
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt)' }}>{i.intake_code}</span>
                        <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{i.enrolled}/{i.capacity}</span>
                      </div>
                      <div className="sms-progress">
                        <div className="sms-progress-fill" style={{
                          width: `${pct}%`,
                          background: pct >= 90 ? 'var(--sms-dot-absent)' : pct >= 70 ? 'var(--sms-dot-late)' : 'var(--sms-acc)'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 5: Recent activity */}
      <div className="row g-3">
        <div className="col-12">
          <div className="sms-card h-100 sms-widget-fade">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>Recent activity</h6>
              <Link to="/system/audit-logs" style={{ fontSize: 12, color: 'var(--sms-acc)', textDecoration: 'none' }}>View all →</Link>
            </div>
            {!recent_audit.length ? <EmptyState message="No activity yet" /> : (
              <div className="d-flex flex-column">
                {recent_audit.map((log, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 py-2"
                    style={{ borderBottom: i < recent_audit.length - 1 ? '1px solid var(--sms-bdr)' : 'none' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: DOT[log.action] || 'var(--sms-txt2)', flexShrink: 0, marginTop: 4 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--sms-txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.description}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 1 }}>
                        {log.username} · {fmtDateTime(log.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
